import type { ChatbotPrompt } from '@repo/types';
import { Card, Icon, Typography } from '@repo/ui';
import { useTinyAIContext } from 'components/TinyAI';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import { useTinyAIPageContext } from 'hooks/useTinyAIPageContext';
import useImpersonate from 'services/auth/useImpersonate';
import { useChatbotPrompts } from 'services/chatbotPrompts/useChatbotPrompts';
import useFeature from 'services/features/useFeature';
import styles from './ConditionDiveDeeperCard.module.css';
import { DIVE_DEEPER_TITLE } from './utils/conditionDetailCopy';

const DISPLAY_LOCATION = 'conditions_detail_page';
const ENTRY_POINT = 'dive_deeper_card';

export const ConditionDiveDeeperCard = ({
  condition,
}: {
  condition?: string;
}) => {
  const { isFeatureEnabled } = useFeature('tiny_ai_chatbot_in_results_portal');
  const { data: prompts } = useChatbotPrompts(DISPLAY_LOCATION);
  const { openChatWithPrompt } = useTinyAIContext();
  const { trackEvent } = useAnalytics();
  const pageContext = useTinyAIPageContext();
  const { impersonateId } = useImpersonate();

  if (!isFeatureEnabled || impersonateId || !prompts || prompts.length === 0) {
    return null;
  }

  const handleClick = (prompt: ChatbotPrompt) => {
    trackEvent('Conditions AI Chatbot Launched', {
      entry_point: ENTRY_POINT,
      question: prompt.prompt_text,
      condition,
    });
    openChatWithPrompt({
      text: prompt.prompt_text,
      displayLocation: DISPLAY_LOCATION,
      promptId: prompt.id,
      pageContext,
    });
  };

  return (
    <Card variant='wrapper'>
      <div className={styles.inner}>
        <div className={styles.header}>
          <Icon name='aiStarsFilled' size='m' color='purple' />
          <Typography variant='heading-m' className={styles.title}>
            {DIVE_DEEPER_TITLE}
          </Typography>
        </div>
        <ul className={styles.promptList}>
          {prompts.map(prompt => (
            <li key={prompt.id}>
              <button
                type='button'
                className={styles.promptButton}
                onClick={() => handleClick(prompt)}
              >
                <span className={styles.bullet} aria-hidden />
                <Typography variant='body-s' className={styles.promptText}>
                  {prompt.prompt_text}
                </Typography>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </Card>
  );
};
